import {useState, useEffect, ChangeEvent, useCallback} from 'react';
import Container from '../../common/layout/Container';
import {setTitle} from '../../../store/header/header.slice';
import {useAppDispatch} from '../../../store/hooks';
import Error from '../../common/Error';
import Spinner from '../../common/Spinner';
import {getAllProcessTypeDetails} from '../../../api/processes';
import Select from '../../common/formik/inputs/Select';
import {
  TargetTimeSettingsResponseProps, DefaultSettingsResponseProps
} from './types';
import {getAllTreatments} from '../../../api/treatments';
import {getTargetTimeSettings, getDefaultSettings} from
  '../../../api/settings/target_task_time';
import CustomConfigurationList from './CustomConfigurationList';
import {dateFormatDDMMYYYY} from '../../../constants/constants';
import dayjs from 'dayjs';
import DefaultSettings from './default_settings/DefaultSettings';
import {useTranslation} from 'react-i18next';
import {OptionsProp} from '../../../constants/types';
import {FormGroup} from '@mui/material';
import {ProcessType} from '../../../api/settings/alert_alarm';

const shouldShowTreatments = (processType) => [
  ProcessType.CLINICAL_EVALUATION,
  ProcessType.TREATMENT_CARE
].includes(processType);

const TargetTimeTaskSettings = () => {
  const {t} = useTranslation('targetTaskTime');

  const defaultSettingsKeys = {
    taskTimeSettings: [
      {
        key: 'DEFAULT_TASK_TIME',
        label: t('taskDefaultTime'),
        withHours: false
      },
      {
        key: 'MIN_TASK_TIME',
        label: t('taskMinTime'),
        withHours: false
      },
      {
        key: 'MAX_TASK_TIME',
        label: t('taskMaxTime'),
        withHours: false
      }
    ],
    processTimeSettings: [
      {
        key: 'DEFAULT_PROCESS_TIME',
        label: t('processDefaultTime'),
        withHours: true
      },
      {
        key: 'MIN_PROCESS_TIME',
        label: t('processMinTime'),
        withHours: true
      },
      {
        key: 'MAX_PROCESS_TIME',
        label: t('processMaxTime'),
        withHours: true
      }
    ]
  };

  const dispatch = useAppDispatch();
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [isTargetTaskTimeSettingsLoading, setIsTargetTaskTimeSettingsLoading] =
    useState(true);
  const [isShowDraftVersion, setIsShowDraftVersion] = useState(false);
  const [error, setError] = useState('');
  const [defaultSettings, setDefaultSettings] =
    useState<DefaultSettingsResponseProps[]>([]);
  const [processTypeOptions, setProcessTypeOptions] =
    useState<OptionsProp[]>([]);
  const [processType, setProcessType] =
    useState<string>(ProcessType.SCHEDULING);
  const [treatmentType, setTreatmentType] =
    useState<string>('');
  const [treatmentOptions, setTreatmentsOptions] = useState<OptionsProp[]>([]);
  const [targetTimeSettings, setTargetTimeSettings] =
    useState<Nullable<TargetTimeSettingsResponseProps>>(null);

  const isVisibleTreatments = shouldShowTreatments(processType);

  const formedResponse = ((code, name) => ({value: code, label: name}));

  useEffect(() => {
    dispatch(setTitle(t('pageTitle')));
  }, []);

  const getTargetTaskTimeSettings = async () => {
    setIsTargetTaskTimeSettingsLoading(true);

    try {
      const requestParams = isVisibleTreatments ? treatmentType : '';

      const targetTimeSettingsResponse =
      await getTargetTimeSettings(processType, requestParams);

      setTargetTimeSettings(targetTimeSettingsResponse);
      setIsShowDraftVersion(targetTimeSettingsResponse.draft.changed);
    } catch (e) {
      setError(e?.message || null);
    } finally {
      setIsTargetTaskTimeSettingsLoading(false);
    }
  };

  useEffect(() => {
    getTargetTaskTimeSettings();
  }, [treatmentType, processType]);

  const getCustomConfigurationSettings = async () => {
    setIsSettingsLoading(true);
    const keys = Object.keys(defaultSettingsKeys)
        .map((key) => defaultSettingsKeys[key]
            .map((item) => item.key));

    try {
      const [
        processTypeDetailsResponse,
        treatmentsResponse,
        defaultSettingsResponse
      ] =
      await Promise.all(
          [
            getAllProcessTypeDetails(),
            getAllTreatments(),
            getDefaultSettings(keys.join())
          ]
      );

      const formedProcessTypeOptions = processTypeDetailsResponse
          .map(({code, description}) => (formedResponse(code, description)));

      const formedTreatmentsOptions =
      treatmentsResponse.map(({rootId, displayName}) =>
        (formedResponse(rootId, displayName)));

      setProcessTypeOptions(formedProcessTypeOptions);

      setTreatmentsOptions(formedTreatmentsOptions);
      setTreatmentType(formedTreatmentsOptions?.[0]?.value || '');
      setDefaultSettings(defaultSettingsResponse);
    } catch (e) {
      setError(e?.message || null);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  useEffect(() => {
    getCustomConfigurationSettings();
  }, []);

  const handleSelectTypeChange = useCallback(({
    target: {value}
  }: ChangeEvent<HTMLSelectElement>) => {
    setProcessType(value);
  }, []);

  const handleSelectTreatmentChange = useCallback(({
    target: {value}
  }: ChangeEvent<HTMLSelectElement>) => {
    setTreatmentType(value);
  }, []);

  const showTreatments = isVisibleTreatments && !!treatmentOptions?.length;

  const currentVersion = () => {
    const presentDate = targetTimeSettings?.activationDate;
    const currentVersion = targetTimeSettings?.current.version;
    const formattedDate = dayjs(presentDate).format(dateFormatDDMMYYYY);
    return `${t('currentVersion')}${currentVersion} (${formattedDate} - ${t('present')})`;
  };

  const draftVersion =
  `${t('draftVersion')}${targetTimeSettings?.draft.version} - ${t('changesSaved')}`;

  return (
    <Container header={t('pageHeader')}>
      <>
        {isSettingsLoading ? (
          <Spinner loadingText={t('loadingText')} />
        ) : (
          <>
            <DefaultSettings
              defaultSettings={defaultSettings}
              defaultSettingsKeys={defaultSettingsKeys}
            />
            <div className="custom-configuration">
              <div className="subheader-wrapper">
                <h2 className="subheader">{t('customConfigurationTitle')}</h2>
                <div className="versions-wrapper">
                  <span className="current-version">{currentVersion()}</span>
                  <span className="draft-version">
                    {isShowDraftVersion && draftVersion}
                  </span>
                </div>
              </div>
              <div className="selectors-container">
                {!!processTypeOptions.length && (
                  <FormGroup>
                    <Select
                      id="processType"
                      name="processType"
                      label={t('process')}
                      size="small"
                      options={processTypeOptions}
                      value={processType || ''}
                      onChange={handleSelectTypeChange}
                    />
                  </FormGroup>
                )}
                {showTreatments && (
                  <FormGroup>
                    <Select
                      options={treatmentOptions}
                      id="treatmentType"
                      name="treatmentType"
                      label={t('treatment')}
                      size="small"
                      value={treatmentType || ''}
                      onChange={handleSelectTreatmentChange}
                    />
                  </FormGroup>
                )}
              </div>
              <CustomConfigurationList
                data={targetTimeSettings}
                isLoading={isTargetTaskTimeSettingsLoading}
                processType={processType}
                treatmentId={treatmentType}
                isVisibleTreatments={isVisibleTreatments}
                setTargetTimeSettings={setTargetTimeSettings}
                setIsShowDraftVersion={setIsShowDraftVersion}
              />
            </div>
          </>
        )}
      </>
      {error && <Error message={error} />}
    </Container>
  );
};

export default TargetTimeTaskSettings;
